import dayjs from "dayjs";
import { api } from "@service/api";

import { Masks } from "@hooks/functions/Masks";
import { ZuToastifyStore } from "@hooks/stores/zustand-toastify-store";
import { useEffect, useState } from "react";
import { Pagination } from "@components/pagination";
import { Card } from "@components/cards/card";
import { FaBroom, FaSort, FaSortDown, FaSortUp } from "react-icons/fa";
import { LoadingSkeleton } from "@components/skeleton-loading";
import { useNavigate } from "react-router-dom";
import { abbreviateName, formatName } from "@hooks/functions/formatName";
import {
  customerStatusSelect,
  CustomerStatusTypes,
  ICustomerSalesmanDTO,
} from "@hooks/dtos/user-dto";
import { CustomerConvertStatus } from "@hooks/functions/Customer";

interface IParams {
  page?: number;
  startDate?: string;
  endDate?: string;
}

type IOrdernation = "ASC" | "DESC" | undefined;

const masks = new Masks();
export const ManagerSalesmanList = () => {
  const navigate = useNavigate();
  const {
    actions: { pushToastify },
  } = ZuToastifyStore();

  // --- Pagination values --- //
  const [page, setPage] = useState<number>(1);
  const [take, setTake] = useState<number>(10);
  const [salesmanAmount, setSalesmanAmount] = useState<number>(0);
  const [totalPages, setTotalPages] = useState<number>(1);

  // --- Filter values --- //
  const [status, setStatus] = useState<CustomerStatusTypes | undefined>();
  const [resultTime, setResultTime] = useState("Todo Período");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [search, setSearch] = useState<string>();
  const [orderDate, setOrderDate] = useState<IOrdernation>();
  const [orderOrder, setOrderOrder] = useState<IOrdernation>();
  const [orderName, setOrderName] = useState<IOrdernation>();

  const [newSearch, setNewSearch] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [salesmen, setSalesmen] = useState<ICustomerSalesmanDTO[]>();

  async function cleanFilters() {
    setSearch("");
    setStartDate("");
    setEndDate("");
    setStatus(undefined);
    setPage(1);
    setTotalPages(1);

    setNewSearch(true);
  }

  const buildFilterParams = () => {
    return Object.fromEntries(
      Object.entries({
        ...(status && { status }),
        ...(startDate && { startDate }),
        ...(endDate && { endDate }),
        ...(orderDate && { orderDate }),
        ...(orderOrder && { orderOrder }),
        ...(search && { search }),
      }),
    );
  };

  const getSalesmenList = async () => {
    setIsLoading(true);
    try {
      const params = `?${new URLSearchParams(
        Object.entries({
          page: (page || 1).toString(),
          take: take.toString(),
          ...buildFilterParams(),
        }),
      ).toString()}`;
      const result = await api.get(`/customer/salesman/list${params}`);
      setSalesmanAmount(result.data.total);
      setSalesmen(result.data.list);
      setTotalPages(result.data.totalPages || 1);
      // console.log({ f: "getSalesmenList", d: result.data, params });

      setResultTime(
        startDate.length + endDate.length > 0
          ? `Período Selecionado`
          : `Todo Período`,
      );

      setNewSearch(false);
      setIsLoading(false);
    } catch (error: any) {
      console.error(error);
      pushToastify({
        message:
          error?.message || "Houve um erro ao consultar o código informado!",
        type: "error",
        theme: "dark",
        pauseOnHover: true,
      });
    }
  };

  const orderStates = {
    date: { value: orderDate, setter: setOrderDate },
    order: { value: orderOrder, setter: setOrderOrder },
    name: { value: orderName, setter: setOrderName },
  };

  const handleOrder = (type: keyof typeof orderStates) => {
    const states: IOrdernation[] = [undefined, "ASC", "DESC"];
    const currentState = orderStates[type].value;
    const setState = orderStates[type].setter;

    const currentIndex = states.indexOf(currentState);
    const nextIndex = (currentIndex + 1) % states.length;
    setState(states[nextIndex]);
  };

  const getOrdenationIcon = (ordernation: IOrdernation) => {
    switch (ordernation) {
      case "ASC":
        return <FaSortUp className="ms-1" />;
      case "DESC":
        return <FaSortDown className="ms-1" />;
      default:
        return <FaSort className="ms-1" />;
    }
  };

  useEffect(() => {
    getSalesmenList();
  }, [page, orderDate, orderOrder, orderName]);

  useEffect(() => {
    if (newSearch) getSalesmenList();
  }, [newSearch]);

  return (
    <>
      {/* Filters Area */}
      <div className="row">
        <div className="col-md-12">
          <Card>
            <div className="card-header">
              <div className="card-title">Filtrar Registros</div>
              <div className="card-category">
                Utilize o filtro para trazer um período ou cliente específico.
              </div>
            </div>
            <div className="card-body">
              {/* Filters */}
              <div className="row">
                {/* Start Date */}
                <div className="col-md-3">
                  <div className="form-group">
                    <label htmlFor="search">Busca</label>
                    <input
                      id="search"
                      type="text"
                      className="form-control"
                      placeholder="Nome, e-mail, documento..."
                      onChange={(e) => {
                        const text = e.target.value;
                        setSearch(text);
                      }}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          getSalesmenList();
                        }
                      }}
                      value={search}
                    />
                  </div>
                </div>
                {/* Start Date */}
                <div className="col-md-3">
                  <div className="form-group">
                    <label htmlFor="startDate">Data Inicial</label>
                    <input
                      type="date"
                      className="form-control"
                      onChange={(e) => setStartDate(e.target.value)}
                      id="startDate"
                      value={startDate}
                    />
                  </div>
                </div>
                {/* End Date */}
                <div className="col-md-3">
                  <div className="form-group">
                    <label htmlFor="endDate">Data Final</label>
                    <input
                      type="date"
                      className="form-control"
                      onChange={(e) => setEndDate(e.target.value)}
                      id="endDate"
                      value={endDate}
                    />
                  </div>
                </div>
                {/* Status */}
                <div className="col-md-3">
                  <div className="form-group">
                    <label htmlFor="status">Status</label>
                    <select
                      className="form-control"
                      value={status || ""}
                      onChange={(e) => {
                        const value = e.target.value;
                        setStatus(
                          value === "0"
                            ? undefined
                            : (value as CustomerStatusTypes),
                        );
                      }}
                    >
                      <option value="">Selecione...</option>
                      {customerStatusSelect.map((x) => {
                        return (
                          <option key={x.status} value={x.status}>
                            {x.text}
                          </option>
                        );
                      })}
                    </select>
                  </div>
                </div>
              </div>
              {/* Filter Buttons */}
              <div className="row">
                <div className="d-flex justify-content-end">
                  <div className="form-group">
                    <button
                      className="btn btn-sm btn-success form-control px-5"
                      onClick={() => getSalesmenList()}
                    >
                      <span className="text-white">Filtrar</span>
                    </button>
                  </div>
                  <div className="form-group">
                    <button
                      className="btn btn-sm btn-warning form-control"
                      onClick={cleanFilters}
                      title="Limpar todos os filtros."
                    >
                      <FaBroom className="text-white" />
                    </button>
                  </div>
                </div>
              </div>
            </div>
            <span className="position-absolute end-0 bg-info text-white p-2 rounded-end rounded-bottom-0">
              {resultTime}
            </span>
          </Card>
        </div>
      </div>

      {/* History Logs Table */}
      <div className="row">
        <div className="col-md-12">
          <Card>
            <div className="card-header">
              <div className="row">
                <div className="col-md-10">
                  <h4 className="card-title">Lista de Consultores</h4>
                  <p className="card-category">Consultores do seu time</p>
                </div>
                {/* <div className="col-md-2 d-flex justify-content-end align-items-center">
                  <button
                    className="btn btn-sm btn-success"
                    onClick={handleExportExcel}
                    disabled={isExporting || isLoading}
                    title="Exportar para Excel"
                  >
                    {isExporting ? (
                      <span className="spinner-border spinner-border-sm text-white" />
                    ) : (
                      <FaFileExcel className="text-white fs-5" />
                    )}
                  </button>
                </div> */}
              </div>
            </div>
            <div className="card-body">
              <div className="table-responsive">
                <div
                  id="basic-datatables_wrapper"
                  className="dataTables_wrapper container-fluid dt-bootstrap4"
                >
                  <div
                    id="basic-datatables_length"
                    className="dataTables_length"
                  >
                    <table className="table table-striped">
                      <thead>
                        <tr role="row">
                          <th
                            style={{ cursor: "pointer" }}
                            onClick={() => {
                              handleOrder("name");
                            }}
                          >
                            Consultor {getOrdenationIcon(orderName)}
                          </th>
                          <th>E-mail</th>
                          <th>Status</th>
                          <th
                            className="d-flex align-items-center"
                            title={`Data do Pedido`}
                            style={{ cursor: "pointer" }}
                            onClick={() => {
                              handleOrder("date");
                            }}
                          >
                            Criação
                            {getOrdenationIcon(orderDate)}
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {isLoading ? (
                          <tr>
                            <td colSpan={10} className="text-center">
                              <LoadingSkeleton />
                            </td>
                          </tr>
                        ) : (
                          <>
                            {salesmen &&
                              salesmen.length > 0 &&
                              salesmen.map((salesman) => {
                                const status = CustomerConvertStatus(
                                  salesman.status,
                                );
                                return (
                                  <tr key={salesman?.id}>
                                    <td>
                                      {formatName(
                                        abbreviateName(
                                          salesman?.name || "",
                                          20,
                                        ),
                                      )}
                                    </td>
                                    <td>{salesman.email || "~~"}</td>
                                    <td title={status.description}>
                                      <span
                                        className={`fs-6 badge badge-${status.color}`}
                                      >
                                        {status.text}
                                      </span>
                                    </td>
                                    <td>
                                      {dayjs(salesman.createdAt).format(
                                        "DD/MM/YYYY",
                                      )}
                                    </td>
                                  </tr>
                                );
                              })}

                            {isLoading ||
                              !salesmen ||
                              (salesmen?.length === 0 && (
                                <tr>
                                  <td colSpan={10} className="text-center">
                                    Nenhum registro encontrado.
                                  </td>
                                </tr>
                              ))}
                          </>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>

              <div className="d-flex justify-content-between align-items-center">
                <span>
                  Quantidade de Registros: {masks.formatMilhar(salesmanAmount)}
                </span>
                <Pagination
                  page={page}
                  setPage={setPage}
                  totalPages={totalPages}
                  maxPageShow={3}
                />
              </div>
            </div>
          </Card>
        </div>
      </div>
    </>
  );
};
